import React from "react";
import PropTypes from "prop-types";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import { formatPaidAtSend, getRecipientKindMeta } from "./messagesUtils";

const BASE = "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap";

/** One hue per recipient kind (README "Status Badges" pattern: light bg / dark translucent bg). */
const KIND_CLASSES = {
  user: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  affiliate:
    "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800",
  partner:
    "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
  external: `${surfaces.tertiary} ${textColors.secondary} ${borders.default}`,
};

/** User / Affiliate / Partner / External chip — the kind snapshotted when the message was enqueued. */
const RecipientKindBadge = ({ kind, className = "" }) => {
  const meta = getRecipientKindMeta(kind);
  const classes = KIND_CLASSES[kind] || KIND_CLASSES.external;
  return (
    <span className={`${BASE} ${classes} ${className}`} title={meta.description || undefined}>
      {meta.label}
    </span>
  );
};

RecipientKindBadge.propTypes = {
  kind: PropTypes.string,
  className: PropTypes.string,
};

const PAID_CLASSES = {
  Paid: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800",
  Free: `${surfaces.tertiary} ${textColors.secondary} ${borders.default}`,
};

/**
 * Paid / Free chip from the `recipient_was_paid` snapshot; renders "—" when
 * the snapshot is unknown (null) so the column never looks empty.
 */
export const PaidChip = ({ value, className = "" }) => {
  const label = formatPaidAtSend(value);
  if (!label) {
    return (
      <span className={`${typography.caption} ${textColors.muted} ${className}`} title="No subscription snapshot for this send">
        —<span className="sr-only">unknown</span>
      </span>
    );
  }
  return (
    <span
      className={`${BASE} ${PAID_CLASSES[label]} ${className}`}
      title={label === "Paid" ? "Had a paid subscription at send time" : "No paid subscription at send time"}
    >
      {label}
    </span>
  );
};

PaidChip.propTypes = {
  value: PropTypes.bool,
  className: PropTypes.string,
};

export default RecipientKindBadge;
